// routes.tsx

import React from "react";
import { useTranslation } from "react-i18next";
import {
  School,
  Replay,
  Analytics,
  PlayCircle,
  Star,
  History,
  Login,
  Public,
  RealEstateAgent,
  HealthAndSafety,
  ShowChart,
  Groups,
  Assignment,
  Security,
  Policy,
} from "@mui/icons-material";
import type { NavbarRoute } from "./interfaces";
import ExamInfoPage from "./pages/ExamInfoPage";
import StudyMaterialsPage from "./pages/StudyMaterialsPage";
import { ExamHistoryListing } from "./pages/ExamHistoryListingPage";
import { ExamResultPage } from "./pages/ExamResultPage";
import { MyDashboardPage } from "./pages/MyDashboardPage";
import { TermsAndConditionsPage } from "./pages/TermsAndConditionsPage";
import { BillingAndRewardsPage } from "./pages/BillingAndRewardsPage";
import { UserProfileSettingsPage } from "./pages/UserProfileSettingsPage";
import ExamCompliancePage from "./pages/ExamCompliancePage";
import ExamCybersecurityPage from "./pages/ExamCybersecurityPage";
import ExamProjectManagementPage from "./pages/ExamProjectManagementPage";
import ExamHumanResourcesPage from "./pages/ExamHumanResourcesPage";
import ExamInsurancePage from "./pages/ExamInsurancePage";
import ExamStateAgentPage from "./pages/ExamStateAgentPage";
import ExamCivilAgentPage from "./pages/ExamCivilAgentPage";
import ExamFinancialRiskPage from "./pages/ExamFinancialRiskPage";
import { SmartRevisionPage } from "./pages/SmartRevisionPage";
import PlaceholderPage from "./pages/PlaceholderPage";

// Lazy-loaded pages
const McqDetailPage = React.lazy(() => import("./pages/McqDetailPage"));
const LoginPage = React.lazy(() => import("./pages/LoginPage"));

export const useTranslatedRoutes = (): NavbarRoute[] => {
  const { t } = useTranslation();

  return [
    {
      key: "login",
      name: t("routes.login"),
      route: "login",
      icon: <Login />,
      component: <LoginPage />,
      isHide: true,
    },
    {
      columns: 1,
      rowsPerColumn: 1,
      icon: <School />,
      key: "mock",
      name: t("routes.mockExams"),
      collapse: [
        {
          key: "mock-submenu",
          name: t("routes.examsActions"),
          collapse: [
            {
              key: "mock-aml",
              name: t("routes.amlCft"),
              route: "mock/aml",
              icon: <Policy />,
              component: <ExamCompliancePage />,
              description: t("routes.amlDescription"),
            },
            {
              key: "mock-cybersecurity",
              name: t("routes.cyberSecurity"),
              route: "mock/cybersecurity",
              icon: <Security />,
              component: <ExamCybersecurityPage />,
              description: " ",
            },
            {
              key: "mock-projectmanagement",
              name: t("routes.projectManagement"),
              route: "mock/projectmanagement",
              icon: <Assignment />,
              component: <ExamProjectManagementPage />,
              description: " ",
            },
            {
              key: "mock-financialrisk",
              name: t("routes.financialRisk"),
              route: "mock/financialrisk",
              icon: <ShowChart />,
              component: <ExamFinancialRiskPage />,
              description: t("routes.financialRiskDescription"),
            },
            {
              key: "mock-humanresources",
              name: t("routes.humanResources"),
              route: "mock/humanresources",
              icon: <Groups />,
              component: <ExamHumanResourcesPage />,
              description: t("routes.humanResourcesDescription"),
            },
            {
              key: "mock-insurance",
              name: t("routes.insurance"),
              route: "mock/insurance",
              icon: <HealthAndSafety />,
              component: <ExamInsurancePage />,
              description: t("routes.insuranceDescription"),
            },
            {
              key: "mock-stateagent",
              name: t("routes.stateAgent"),
              route: "mock/stateagent",
              icon: <RealEstateAgent />,
              component: <ExamStateAgentPage />,
              description: t("routes.stateAgentDescription"),
            },
            {
              key: "mock-civilagent",
              name: t("routes.civilAgent"),
              route: "mock/civilagent",
              icon: <Public />,
              component: <ExamCivilAgentPage />,
              description: t("routes.civilAgentDescription"),
            },
            {
              key: "tnc",
              name: t("routes.termsAndConditions"),
              route: "tnc",
              icon: <PlayCircle />,
              component: <TermsAndConditionsPage />,
              description: t("routes.tncDescription"),
              isHide: true,
            },
            {
              key: "mock-professioal-exam-papers",
              name: t("routes.hksiExam"),
              route: "mock/quiz/:quizName",
              icon: <PlayCircle />,
              component: <ExamInfoPage />,
              description: "",
              isHide: true,
            },
            {
              key: "mock-professioal-exam-mcq",
              name: t("routes.startNewMockExam"),
              route: "mock/quiz/:quizName/mcq",
              icon: <PlayCircle />,
              component: <McqDetailPage />,
              description: "",
              isHide: true,
            },
            {
              key: "mock-professioal-exam-materials",
              name: "",
              route: "mock/quiz/:quizName/materials",
              icon: <PlayCircle />,
              component: <StudyMaterialsPage />,
              description: "",
              isHide: true,
            },
            {
              key: "mock-professioal-exam-result",
              name: "",
              route: "mock/quiz/result/:quizName/:submissionId",
              icon: <PlayCircle />,
              component: <ExamResultPage />,
              description: "",
              isHide: true,
            },
          ],
        },
      ],
    },
    {
      columns: 1,
      rowsPerColumn: 1,
      icon: <Replay />,
      key: "revision",
      name: t("routes.revision"),
      collapse: [
        {
          key: "revision-submenu",
          name: t("routes.revisionTools"),
          collapse: [
            {
              key: "prev-attempts",
              name: t("routes.yourMockHistories"),
              route: "revision/history",
              icon: <History />,
              component: <ExamHistoryListing />,
              description: t("routes.reviewPastMocks"),
            },
            {
              key: "smart-rev",
              name: t("routes.smartRevisionEngine"),
              route: "revision/engine",
              icon: <Replay />,
              component: <SmartRevisionPage />,
              description: t("routes.aiPoweredSpacedRepetition"),
            },
            {
              key: "quick-rev",
              name: t("routes.quickRevisionSets"),
              route: "revision/quick",
              icon: <Star />,
              component: <PlaceholderPage />,
              description: t("routes.quickRevisionDescription"),
            },
          ],
        },
      ],
    },
    {
      columns: 1,
      rowsPerColumn: 1,
      icon: <Analytics />,
      key: "my",
      name: t("routes.my"),
      collapse: [
        {
          key: "my-progress",
          name: t("routes.myProgress"),
          collapse: [
            {
              key: "my-dashboard",
              name: t("routes.myDashboard"),
              route: "my/dashboard",
              icon: <Analytics />,
              component: <MyDashboardPage />,
              description: t("routes.myDashboard"),
            },
            {
              key: "my-reward",
              name: t("routes.myReward"),
              route: "my/reward",
              icon: <Analytics />,
              component: <BillingAndRewardsPage />,
              description: t("routes.myReward"),
            },
            {
              key: "mastery",
              name: t("routes.topicMastery"),
              route: "progress/mastery",
              icon: <Star />,
              component: <PlaceholderPage />,
              description: t("routes.heatmapAndInsights"),
            },
            {
              key: "predictions",
              name: t("routes.predictedScores"),
              route: "progress/predictions",
              icon: <PlayCircle />,
              component: <PlaceholderPage />,
              description: t("routes.aiScorePrediction"),
            },
            {
              key: "my-profile",
              name: t("routes.myProfile"),
              route: "my/profile",
              icon: <Analytics />,
              component: <UserProfileSettingsPage />,
              description: t("routes.myProfile"),
            },
          ],
        },
      ],
    },
  ];
};

export default useTranslatedRoutes;
